import React, { useMemo } from 'react'
import { Elements, PaymentElement } from '@stripe/react-stripe-js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import BackLink from '../BackLink'
import PageHeader from '../PageHeader'
import Tipping from '../Tipping'
import stripeAppearance from '../stripeAppearance'
import usePayment from '../usePayment'
import { toStripeMinorUnits } from '../stripeIntents'

// Final payment step for credit_card and bank. Wraps the form in its own
// inner <Elements> with payment-mode options (the outer StripeWrapper
// only carries appearance/fonts). Keyed on currency so a Start-step
// currency change forces a remount; recurring vs. one-time changes the
// `mode` (subscription / payment) which Stripe doesn't allow updating in
// place, but the panel transition between Start ↔ Payment unmounts the
// inner Elements scope, so the next entry mounts fresh with the current
// mode. paymentMethodTypes is recomputed on paymentMethod change so
// React-Stripe can update the mounted Elements to expose the bank
// inputs (and inline Financial Connections / mandate) when the donor
// switched to bank on the picker.
//
// The inner Elements scope hosts the <PaymentElement> below; the
// AddressElement on Contact lives in the outer StripeWrapper scope and
// shares state with us through donation.state.address (set via
// AddressElement's onChange in Contact). Stripe's autofill coordination
// between AddressElement and PaymentElement only fires when they sit in
// the same Elements scope, which we deliberately don't do — the panels
// live in different React subtrees and we don't want to fork
// Panel.Collection on payment method.
const StripePayment = ({
  i18n, donation, record, session, partnerItem,
  paymentMethod, creditCardProps, bankProps,
  stripePromise, stripeFonts,
  tippingEnabled, coverCostsEnabled, pledgeCoversFees,
  finished, setSubmitInFlight, onBack,
}) => {
  // Recurring donations route through SetupIntent; the inner Elements
  // mode must match so the wallet sheets / mandate copy / future-charge
  // disclosures render correctly.
  const isSubscription = donation.state.subscription !== 'one_time'
  const elementsOptions = useMemo(() => ({
    mode: isSubscription ? 'subscription' : 'payment',
    amount: Math.max(toStripeMinorUnits(donation.total, donation.currency), 1),
    currency: donation.currency.iso_code.toLowerCase(),
    paymentMethodTypes: [ paymentMethod === 'bank' ? 'us_bank_account' : 'card' ],
    appearance: stripeAppearance(),
    fonts: stripeFonts,
    locale: i18n.locale,
  }), [
    isSubscription, donation.total,
    donation.currency.iso_code, donation.currency.exponent,
    paymentMethod, stripeFonts, i18n.locale,
  ])

  return (
    <Elements
      key={donation.currency.iso_code}
      stripe={stripePromise}
      options={elementsOptions}
    >
      <StripePaymentForm
        i18n={i18n}
        donation={donation}
        record={record}
        session={session}
        partnerItem={partnerItem}
        paymentMethod={paymentMethod}
        creditCardProps={creditCardProps}
        bankProps={bankProps}
        tippingEnabled={tippingEnabled}
        coverCostsEnabled={coverCostsEnabled}
        pledgeCoversFees={pledgeCoversFees}
        finished={finished}
        setSubmitInFlight={setSubmitInFlight}
        onBack={onBack}
      />
    </Elements>
  )
}

const StripePaymentForm = ({
  i18n, donation, record, session, partnerItem,
  paymentMethod, creditCardProps, bankProps,
  tippingEnabled, coverCostsEnabled, pledgeCoversFees,
  finished, setSubmitInFlight, onBack,
}) => {
  const payment = usePayment({
    i18n, donation, record, partnerItem,
    paymentMethod, creditCardProps, bankProps,
    finished, setSubmitInFlight,
  })

  const fullName = [
    donation.state.firstName,
    donation.state.lastName,
  ].join(' ').trim()

  const handleSubmit = e => {
    e.preventDefault()
    if (!donation.validateTip()) return
    payment.submit()
  }

  // usePayment normalizes errors into { base: [...] } or { stripe: [...] }
  // shapes. Only base errors are surfaced here — Stripe-flavored errors
  // (validation, card declines, etc.) are already rendered inline by the
  // PaymentElement itself, and showing them again above the submit button
  // would double them up.
  const errorMessage = payment.errors?.base?.[0]

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />

      {/* Suppress the Payment Element's own wallet rendering; AP/GP
          live on the dedicated express checkout step (Phase 5). Name is
          always collected on Contact, and email when session.collect_email
          is on — hide those fields here and seed defaults so Stripe still
          attaches the values to the resulting payment method.

          When the AddressElement on Contact captured a billing address
          (donation.collectAddress), suppress the PaymentElement's
          address block entirely and seed the donor's full address as
          defaults. Without this, PaymentElement's 'auto' default
          re-asks for postal code (and country, for non-US) on the
          card form even though the donor already typed their full
          address one step earlier.

          When AddressElement isn't used, leave fields.billingDetails.address
          on its default — Stripe rejects card payments without a
          postal code / country present somewhere, so we let
          PaymentElement collect them inline. */}
      <PaymentElement
        options={{
          wallets: { applePay: 'never', googlePay: 'never' },
          fields: {
            billingDetails: {
              name: 'never',
              email: session.collect_email ? 'never' : 'auto',
              ...(donation.collectAddress && { address: 'never' }),
            },
          },
          defaultValues: {
            billingDetails: {
              name: fullName || undefined,
              email: donation.state.email || undefined,
              ...(donation.collectAddress && donation.state.address && {
                address: donation.state.address,
              }),
            },
          },
        }}
      />

      {/* Tipping/cover-costs sits at the bottom — donor enters payment
          details first, then makes the optional adjustments directly
          above the submit. The submit button itself carries the
          donate_total_recurrence text, so there's no separate total
          line. Tipping renders nothing when neither feature is
          enabled, so no caller-side gate is needed. */}
      <Tipping
        i18n={i18n}
        donation={donation}
        partnerItem={partnerItem}
        tippingEnabled={tippingEnabled}
        coverCostsEnabled={coverCostsEnabled}
        pledgeCoversFees={pledgeCoversFees}
      />

      {errorMessage && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm"
        >
          {errorMessage}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="submit"
          disabled={payment.processing}
          className="btn btn-primary flex w-full items-center justify-center gap-2"
        >
          {payment.processing && <FontAwesomeIcon icon={faCircleNotch} spin />}
          {/* Stand-in for the OrderSummary's donation+total rows on
              the Stripe step. AP/GP show the breakdown in the wallet
              sheet; PayPal/Venmo show the total in their own SDK UI;
              Stripe gets it in the submit button. Mirrors v6
              fiat/SubmitButton.jsx's CommonButton text. .trim() drops
              the trailing space when one_time has no .submit suffix. */}
          {i18n.t('donate_total_recurrence', {
            total: i18n.formatCurrency(donation.total, donation.currency),
            recurrence: i18n.t(`subscription_labels.${donation.state.subscription}.submit`),
          }).trim()}
        </button>
        <BackLink i18n={i18n} onClick={onBack} disabled={payment.processing} />
      </div>
    </form>
  )
}

export default StripePayment
